import React, { useMemo, useState } from "react";
import { NetworkStatus, useQuery } from "@apollo/client";
import { Alert, alpha, Box, Button, Chip, CircularProgress, Link, Paper, Stack, Typography } from "@mui/material";
import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import { Link as RouterLink } from "react-router-dom";

import { describeError } from "../../common/rateLimit";
import { USER_ACTIVITY } from "./userDetails.queries";
import {
  ACTIVITY_KINDS,
  ActivityEntry,
  ActivityKind,
  activityByDay,
  countOf,
  foldScreens,
  screenTrail,
} from "./userDetailsModel";

const PAGE = 50;

const ICONS: Record<ActivityKind, { Icon: typeof AppsOutlinedIcon; tint: string }> = {
  ACTION: { Icon: ThumbUpOutlinedIcon, tint: "#16a34a" },
  APP: { Icon: AppsOutlinedIcon, tint: "#2563eb" },
  MESSAGE: { Icon: NotificationsNoneOutlinedIcon, tint: "#d97706" },
};

const time = (value: string) =>
  new Date(value).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

const Row: React.FC<{ kind: ActivityKind; at: string; children: React.ReactNode }> = ({ kind, at, children }) => {
  const { Icon, tint } = ICONS[kind];

  return (
    <Box component="li" sx={{ display: "grid", gridTemplateColumns: "32px 1fr auto", columnGap: 1.5, py: 1.25 }}>
      <Box
        aria-hidden
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          bgcolor: alpha(tint, 0.12),
          color: tint,
          "& svg": { fontSize: 17 },
        }}
      >
        <Icon />
      </Box>
      <Box sx={{ minWidth: 0 }}>{children}</Box>
      <Typography
        variant="caption"
        color="text.secondary"
        component="time"
        dateTime={at}
        sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", pt: 0.25 }}
      >
        {time(at)}
      </Typography>
    </Box>
  );
};

/** The title, with what it names as a link when the dashboard has a page for it. */
const Title: React.FC<{ entry: ActivityEntry }> = ({ entry }) => {
  const named = entry.businessName ?? entry.otherUserName;
  const to = entry.businessPlaceId
    ? `/businesses/${entry.businessPlaceId}`
    : entry.otherUserId
    ? `/users/${entry.otherUserId}`
    : null;

  if (!named || !to || !entry.title.endsWith(named)) return <>{entry.title}</>;

  return (
    <>
      {entry.title.slice(0, -named.length)}
      <Link component={RouterLink} to={to} underline="hover">
        {named}
      </Link>
    </>
  );
};

/**
 * One person's history, newest first. Asked for when the tab is opened, then a
 * page at a time on request: nothing polls.
 */
export const UserActivityFeed: React.FC<{ userId: number }> = ({ userId }) => {
  /** Every kind by default. The query leaves `kinds` out then, the same as asking for all three. */
  const [kinds, setKinds] = useState<ActivityKind[]>(ACTIVITY_KINDS.map((item) => item.kind));

  const { data, error, networkStatus, fetchMore } = useQuery(USER_ACTIVITY, {
    variables: { userId, kinds, take: PAGE },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
    notifyOnNetworkStatusChange: true,
  });

  const page = data?.adminUserActivity;
  const days = useMemo(() => activityByDay(foldScreens(page?.items ?? [])), [page]);
  const loadingMore = networkStatus === NetworkStatus.fetchMore;
  const loadingFirst = !page && networkStatus !== NetworkStatus.error;

  const loadMore = () =>
    fetchMore({
      variables: { after: page.endCursor },
      updateQuery: (previous, { fetchMoreResult }) => ({
        adminUserActivity: {
          ...fetchMoreResult.adminUserActivity,
          items: [...previous.adminUserActivity.items, ...fetchMoreResult.adminUserActivity.items],
        },
      }),
    });

  const toggle = (kind: ActivityKind) =>
    setKinds((current) => {
      // The last one stays on: the API refuses an empty list, and an empty feed would say nothing.
      if (current.includes(kind)) return current.length === 1 ? current : current.filter((k) => k !== kind);
      return ACTIVITY_KINDS.map((item) => item.kind).filter((k) => k === kind || current.includes(k));
    });

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1}
        sx={{ mb: 1 }}
      >
        <Typography variant="h6">Activity</Typography>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {ACTIVITY_KINDS.map(({ kind, label }) => {
            const on = kinds.includes(kind);
            return (
              <Chip
                key={kind}
                size="small"
                label={label}
                color={on ? "primary" : "default"}
                variant={on ? "filled" : "outlined"}
                aria-pressed={on}
                onClick={() => toggle(kind)}
              />
            );
          })}
        </Stack>
      </Stack>

      {error && <Alert severity="error">{describeError(error, "Could not load this person's activity.")}</Alert>}

      {loadingFirst && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={24} aria-label="Loading activity" />
        </Box>
      )}

      {page && !days.length && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          Nothing to show here yet.
        </Typography>
      )}

      {days.map((day) => (
        <Box key={day.heading} component="section" aria-label={day.heading} sx={{ mt: 1.5 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            component="h3"
            sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}
          >
            {day.heading}
          </Typography>
          <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
            {day.rows.map((row) =>
              row.row === "entry" ? (
                <Row key={row.id} kind={row.entry.kind} at={row.entry.occurredAt}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    <Title entry={row.entry} />
                  </Typography>
                  {row.entry.note && (
                    <Typography variant="caption" color="text.secondary" component="p">
                      {row.entry.note}
                    </Typography>
                  )}
                  {row.entry.body && (
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.5,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        bgcolor: "#f8fafc",
                        border: "1px solid #eef1f5",
                        borderRadius: 1.5,
                        px: 1.25,
                        py: 0.75,
                      }}
                    >
                      {row.entry.body}
                    </Typography>
                  )}
                </Row>
              ) : (
                <Row key={row.id} kind="APP" at={row.endedAt}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Viewed {countOf(row.screens.length, "screen")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="p" sx={{ wordBreak: "break-word" }}>
                    {screenTrail(row.screens)}
                  </Typography>
                </Row>
              )
            )}
          </Box>
        </Box>
      ))}

      {page?.hasNextPage && (
        <Box sx={{ mt: 1.5, textAlign: "center" }}>
          <Button size="small" variant="outlined" disabled={loadingMore} onClick={loadMore}>
            {loadingMore ? "Loading…" : "Show older"}
          </Button>
        </Box>
      )}
    </Paper>
  );
};
